import * as RD from '@radix-ui/react-dialog';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCompanies, useContacts, useThreads } from '@/api/hooks';
import type { Me } from '@/api/session';
import { Icon } from '@/ui/icons';
import { IconButton } from '@/ui/primitives';
import { useFrame } from './frame';
import { useDepartmentNames } from './departments';
import { useNav } from './NavRail';
import { doorsOf } from './nav';

interface Hit {
  key: string;
  group: 'Screens' | 'Records' | 'Conversations' | 'Ask';
  label: string;
  sub?: string;
  run: () => void;
}

/**
 * The gallery's command palette (G:468-498): ⌘K from anywhere. Screens,
 * records and conversations by name; and a sentence that matches nothing is
 * a question for your twin, which the person still sends themselves.
 */
/** Fired by anything that wants the palette open without a keyboard. */
export const OPEN_PALETTE = 'norum:open-palette';

export function CommandPalette({ me }: { me: Me }) {
  const frame = useFrame();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [at, setAt] = useState(0);
  const needle = q.trim().toLowerCase();
  const searching = open && needle.length >= 2;
  const companies = useCompanies(searching ? needle : '');
  const contacts = useContacts(searching ? needle : '');
  const threads = useThreads(me.department);
  // The screens are the rail's own doors, so the palette never offers one
  // the rail withholds (a superadmin's Workspace, Finance's Mail).
  const screens = doorsOf(useNav(me, useDepartmentNames()));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    // A phone has no ⌘K: the header's menu asks for the palette by name.
    const onAsk = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE, onAsk);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE, onAsk);
    };
  }, []);

  const go = (to: string) => {
    setOpen(false);
    void navigate(to,{state:{companyReturn:null}});
  };

  const hits = useMemo<Hit[]>(() => {
    const out: Hit[] = [];
    for (const s of screens) {
      if (!needle || s.label.toLowerCase().includes(needle)) out.push({ key: `s:${s.to}`, group: 'Screens', label: s.label, run: () => go(s.to) });
    }
    if (searching) {
      for (const c of (companies.data?.companies ?? []).slice(0, 5)) {
        out.push({ key: `co:${c.id}`, group: 'Records', label: c.displayName ?? c.legalName, sub: 'Company', run: () => go(`/companies/${c.id}`) });
      }
      for (const c of (contacts.data?.contacts ?? []).slice(0, 5)) {
        out.push({ key: `ct:${c.id}`, group: 'Records', label: c.fullName, sub: 'Contact', run: () => go(`/contacts/${c.id}`) });
      }
      for (const t of (threads.data?.threads ?? []).filter((t) => (t.subject ?? '').toLowerCase().includes(needle)).slice(0, 5)) {
        out.push({ key: `th:${t.id}`, group: 'Conversations', label: t.subject ?? 'A conversation', run: () => go(`/mail/${t.id}`) });
      }
    }
    if (needle.length > 0) {
      out.push({
        key: 'ask',
        group: 'Ask',
        label: `Ask ${frame.twin ?? 'your twin'}: “${q.trim()}”`,
        run: () => {
          setOpen(false);
          frame.ask(q.trim());
        },
      });
    }
    return out;
  }, [needle, searching, companies.data, contacts.data, threads.data, frame.twin, q, screens]);

  useEffect(() => setAt(0), [needle]);

  return (
    <RD.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ('');
      }}
    >
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)]" />
        <RD.Content
          className="fixed left-1/2 top-[12dvh] z-50 flex max-h-[70dvh] w-[calc(100%-32px)] max-w-[600px] -translate-x-1/2 flex-col border border-strong bg-page shadow-toast"
          aria-describedby={undefined}
        >
          <RD.Title className="sr-only">Go to, or ask</RD.Title>
          <label className="flex items-center gap-3 border-b border-line px-4 text-meta">
            <Icon glyph="search" />
            <input
              autoFocus
              aria-label="Go to, or ask"
              placeholder="Go to a screen, a record, a conversation — or ask"
              className="min-h-[52px] min-w-0 flex-1 border-0 bg-transparent text-body text-ink outline-none placeholder:text-meta"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setAt((i) => Math.min(hits.length - 1, i + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setAt((i) => Math.max(0, i - 1));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  hits[at]?.run();
                }
              }}
            />
            <span className="text-micro uppercase tracking-label phone:hidden">Esc</span>
            <RD.Close asChild>
              <IconButton label="Close" glyph="close" ghost className="hidden phone:inline-flex" />
            </RD.Close>
          </label>
          <ul role="listbox" aria-label="Results" className="m-0 flex list-none flex-col overflow-y-auto p-0">
            {hits.map((h, i) => (
              <li key={h.key} role="presentation">
                {(i === 0 || hits[i - 1]!.group !== h.group) && (
                  <span className="block px-4 pb-1.5 pt-3 text-micro uppercase tracking-rail text-meta">{h.group}</span>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === at}
                  onMouseEnter={() => setAt(i)}
                  onClick={h.run}
                  className={`flex min-h-tap w-full items-center gap-3 px-4 text-left text-dense ${i === at ? 'bg-sunken' : ''}`}
                >
                  <span className="min-w-0 flex-1 truncate">{h.label}</span>
                  {h.sub && <span className="text-label uppercase tracking-label text-meta">{h.sub}</span>}
                </button>
              </li>
            ))}
            {hits.length === 0 && <li className="px-4 py-5 text-dense text-meta">Type to go somewhere, or to ask.</li>}
          </ul>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
